<template>
  <v-data-table
    :headers="headers"
    :items="filteredProducts"
    multi-sort
    item-key="name"
    :expanded.sync="expanded"
    :single-expand="singleExpand"
    :items-per-page="50"
    :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
    :options.sync="options"
  >
    <!-- custom headers -->
    <template v-slot:header.price_1>
      <div class="d-flex align-center">
        <span style="display: inline-block">Preço 1</span>
        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-d10" class="pl-1" style="display: table-cell; vertical-align: middle">info</mf-icon>
            </span>
          </template>
          <span>O preço 1 é utilizado apenas para exibição da oferta no aplicativo. O preço 1 não é aplicado no PDV.</span>
        </v-tooltip>
      </div>
    </template>

    <template v-slot:header.minimum_per_client="{ header }">
      <tooltip :disabled="!header.tooltip" :message="header.tooltip">
        {{ header.text }}
        <mf-icon v-show="header.tooltip" small color="grey">info</mf-icon>
      </tooltip>
    </template>

    <template v-slot:header.max_coupons="{ header }">
      <tooltip :disabled="!header.tooltip" :message="header.tooltip">
        {{ header.text }}
        <mf-icon v-show="header.tooltip" small color="grey">info</mf-icon>
      </tooltip>
    </template>

    <!-- group content -->
    <template v-if="type === 'grouped' && !isRevision" v-slot:body.prepend>
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td>
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('name')"
            v-model="group.name"
            :error="(validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].name.$invalid) || group.name === ''"
            :text="true"
            :column="0"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage"
          />
          <span v-else>{{ group.name }}</span>
        </td>

        <td v-if="offerSalesType === 'cashback_percentage_offer_sales'">
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.price_1"
            money
            :column="1"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].price_1.$invalid"
          />
          <span v-else>R$ {{ localeString(group.price_1 || 0, 2) }}</span>
        </td>

        <td v-else-if="!isIndustryCampaign">
          <field-toggle
            v-if="!isRevision"
            v-model="group.price"
            money
            :column="1"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].price.$invalid"
          />
          <span v-else>R$ {{ localeString(group.price || 0, 2) }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('cashbackAbsolute')"
            v-model="group[offerSalesTypeField]"
            :money="offerSalesType === 'cashback_absolute_offer_sales' || offerSalesType === 'next_buy_cashback_offer_sales'"
            :percentage="offerSalesType !== 'cashback_absolute_offer_sales' && offerSalesType !== 'next_buy_cashback_offer_sales'"
            :limit-value="offerSalesType === 'cashback_percentage_offer_sales' ? 100 : 0"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex][offerSalesTypeField].$invalid"
            :column="2"
            :row="groupIndex + 9999"
          />
          <span v-else-if="isRevision && offerSalesType !== 'cashback_absolute_offer_sales' && offerSalesType !== 'next_buy_cashback_offer_sales'">{{
            group[offerSalesTypeField] ? `${localeString(group[offerSalesTypeField], 0)}%` : '-'
          }}</span>
          <span v-else>{{ group[offerSalesTypeField] ? `R$${localeString(group[offerSalesTypeField], 2)}` : '-' }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('minimumPerClient')"
            v-model="group.minimum_per_client"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].minMaxPerClientValidation.$invalid"
            :column="3"
            :row="groupIndex + 9999"
            :tooltip-text="minAndMaxPerClientTooltipMessage"
            :disabled="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
            :tooltip="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
          />
          <span v-else-if="isRevision && !group.minimum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ group.minimum_per_client }}</span>
        </td>

        <td v-if="offerSalesType !== 'next_buy_cashback_offer_sales'">
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('maximumPerClient')"
            v-model="group.maximum_per_client"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].minMaxPerClientValidation.$invalid"
            :column="4"
            :row="groupIndex + 9999"
            :tooltip-text="minAndMaxPerClientTooltipMessage"
            :disabled="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
            :tooltip="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
          />
          <span v-else-if="isRevision && !group.maximum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ group.maximum_per_client }}</span>
        </td>

        <td v-else>
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('maximumPerClient')"
            v-model="group.max_coupons"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].minMaxPerClientValidation.$invalid"
            :column="4"
            :row="groupIndex + 9999"
            :tooltip-text="minAndMaxPerClientTooltipMessage"
            :disabled="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
            :tooltip="disableMinAndMaxCpf || group.group_rules_exhibition === 'PUBLIC'"
          />
          <span v-else-if="isRevision && !group.max_coupons" class="empty-value">Vazio</span>
          <span v-else>{{ group.max_coupons }}</span>
        </td>
        <td></td>
        <td></td>
      </tr>
    </template>

    <!-- table content -->
    <template v-slot:item="{ item, expand, isExpanded, index }">
      <tr :class="{ repeated: addErrorClass(item.sku) }">
        <td>
          <div v-if="isMultipleEans(item)">
            <mf-button type="icon" size="sm" color="marca-1" :icon="isExpanded ? 'indeterminate_check_box' : 'add_box'" @click="expand(!isExpanded)" />
          </div>
        </td>

        <td>
          <div v-if="type === 'grouped' ? group.group_rules_exhibition === 'PRIVATE' : true" class="img-container d-flex align-center justify-center">
            <img v-if="item.image_url && item.image_url.length" :src="item.image_url" alt="" width="100%" />
            <mf-icon v-else>image_not_supported</mf-icon>
          </div>
        </td>

        <td>
          {{ item.sku }}
        </td>

        <td>
          <div class="py-0" :class="helpers.wrongEAN[getIndex(item.sku)] ? 'invalid-input' : ''">
            <span v-if="item.product && item.product.eans && item.product.eans.length > 1">
              {{ getEansCount(item) }}
            </span>
            <span v-else style="word-break: keep-all">
              {{ item.ean }}
            </span>
          </div>
        </td>

        <td>
          <field-toggle
            v-if="!item.products && !disableIfIsGrouped && !isRevision && !disabled && !isDisabledInRulesByType('name')"
            v-model="item.name"
            :error="
              (type === 'ungrouped' && validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].name.$invalid) ||
              item.name === ''
            "
            :text="true"
            :column="0"
            :row="index"
          />
          <span v-else>{{ item.name }}</span>
        </td>

        <td v-if="offerSalesType === 'cashback_percentage_offer_sales'">
          <div v-if="type === 'ungrouped'">
            <field-toggle
              v-if="!disableIfIsGrouped && !isRevision && !disabled"
              v-model="item.price_1"
              money
              :error="type === 'ungrouped' && validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].price_1.$invalid"
              :column="1"
              :row="index"
            />
            <span v-else-if="isRevision && !item.price_1" class="empty-value">Vazio</span>
            <span v-else>R$ {{ localeString(item.price_1 || 0, 2) }}</span>
          </div>
          <span v-else-if="type !== 'ungrouped' && isRevision && !group.price_1" class="empty-value">Vazio</span>
          <span v-else>R$ {{ localeString(group.price_1 || 0, 2) }}</span>
        </td>

        <td v-else-if="!isIndustryCampaign">
          <div v-if="type === 'ungrouped'">
            <field-toggle
              v-if="!disableIfIsGrouped && !isRevision && !disabled"
              v-model="item.price"
              money
              :error="type === 'ungrouped' && validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].price.$invalid"
              :column="1"
              :row="index"
            />
            <span v-else-if="isRevision && !item.price" class="empty-value">Vazio</span>
            <span v-else>R$ {{ localeString(item.price || 0, 2) }}</span>
          </div>
          <span v-else-if="type !== 'ungrouped' && isRevision && !group.price" class="empty-value">Vazio</span>
          <span v-else>R$ {{ localeString(group.price || 0, 2) }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!isRevision && !disabled && !isDisabledInRulesByType('cashbackAbsolute')"
            v-model="item[offerSalesTypeField]"
            :money="offerSalesType === 'cashback_absolute_offer_sales' || offerSalesType === 'next_buy_cashback_offer_sales'"
            :percentage="offerSalesType !== 'cashback_absolute_offer_sales' && offerSalesType !== 'next_buy_cashback_offer_sales'"
            :limit-value="offerSalesType === 'cashback_percentage_offer_sales' ? 100 : 0"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)][offerSalesTypeField].$invalid"
            :column="2"
            :row="index"
          />
          <span v-else-if="isRevision && offerSalesType !== 'cashback_absolute_offer_sales' && offerSalesType !== 'next_buy_cashback_offer_sales'">{{
            item[offerSalesTypeField] ? `${localeString(item[offerSalesTypeField], 0)}%` : '-'
          }}</span>
          <span v-else>{{ item[offerSalesTypeField] ? `R$${localeString(item[offerSalesTypeField], 2)}` : '-' }}</span>
        </td>

        <td v-else>
          <span v-if="offerSalesType !== 'cashback_absolute_offer_sales' && offerSalesType !== 'next_buy_cashback_offer_sales'">{{
            group[offerSalesTypeField] ? `${localeString(group[offerSalesTypeField], 0)}%` : '-'
          }}</span>
          <span v-else>{{ group[offerSalesTypeField] ? `R$${localeString(group[offerSalesTypeField], 2)}` : '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled && !isDisabledInRulesByType('minimumPerClient')"
            v-model="item.minimum_per_client"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].minMaxPerClientValidation.$invalid"
            :column="3"
            :row="index"
          />
          <span v-else-if="isRevision && !item.minimum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ item.minimum_per_client }}</span>
        </td>

        <td v-else>
          <span v-if="isRevision && !group.minimum_per_client && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">Vazio</span>
          <span v-else>{{ group.minimum_per_client || '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped' && offerSalesType !== 'next_buy_cashback_offer_sales'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled && !isDisabledInRulesByType('maximumPerClient')"
            v-model="item.maximum_per_client"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].minMaxPerClientValidation.$invalid"
            :column="4"
            :row="index"
          />
          <span v-else-if="isRevision && !item.maximum_per_client && !item.max_coupons" class="empty-value">Vazio</span>
          <span v-else-if="offerSalesType === 'next_buy_cashback_offer_sales'">{{ item.max_coupons }}</span>
          <span v-else>{{ item.maximum_per_client }}</span>
        </td>

        <td v-else-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled && !isDisabledInRulesByType('maximumPerClient')"
            v-model="item.max_coupons"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].minMaxPerClientValidation.$invalid"
            :column="4"
            :row="index"
          />
          <span v-else-if="isRevision && !item.max_coupons" class="empty-value">Vazio</span>
          <span v-else>{{ item.max_coupons }}</span>
        </td>

        <td v-else>
          <span v-if="isRevision && !group.maximum_per_client && !item.max_coupons && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">
            Vazio
          </span>
          <span v-else-if="offerSalesType === 'next_buy_cashback_offer_sales'">{{ group.max_coupons }}</span>
          <span v-else>{{ group.maximum_per_client || '-' }}</span>
        </td>

        <td v-if="isFranchise && isRevision">
          <div class="d-flex align-center">
            <mf-icon :color="getFranchiseeColor(item.franchisee_eans_frequency_percentage || 0)" size="16" fill class="mr-2" icon="radio_button_unchecked" />
            {{ item.franchisee_eans_frequency_percentage || 0 }}%
          </div>
        </td>

        <td>
          <tooltip :disabled="isTooltipChangingStatusDisabled" :message="tooltipMessage">
            <mf-toggle v-model="item.active" :disabled="isRevision || disabled || isDisabledInRulesByType('active')" class="pa-0 ml-2" hide-details />
          </tooltip>
        </td>

        <td v-if="!isRevision">
          <tooltip :disabled="!isIndustryCampaign" :message="tooltipMessage">
            <mf-button
              type="icon"
              icon="delete"
              color="cinza-l30"
              :disabled="isRevision || isIndustryCampaign || item.disabled || disabled"
              @click="removeItem(item)"
            />
          </tooltip>
        </td>
      </tr>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td class="expanded-itens" :colspan="headers.length">
        <v-checkbox
          v-for="ean in toSet(item.product.eans)"
          :key="ean"
          v-model="item.eans"
          :value="ean"
          :label="ean"
          :disabled="isCheckboxDisabled(ean, item.eans) || isRevision || disabled"
          color="marca-1"
          multiple
        />
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { offerSalesTables } from '../../../mixins/offer-sales-tables'

export default {
  name: 'CashbackAndPointsTable',
  components: {
    FieldToggle: () => import('../../FieldToggle.vue'),
    Tooltip: () => import('@/modules/campanhas/gestao-ofertas-v2/views/components/Tooltip.vue'),
  },
  mixins: [offerSalesTables],
  props: {
    offerSalesType: {
      type: String,
      required: true,
      validator: (val) =>
        ['cashback_percentage_offer_sales', 'cashback_absolute_offer_sales', 'points_offer_sales', 'next_buy_cashback_offer_sales'].includes(val),
    },
  },
  data: () => ({
    expanded: [],
    singleExpand: true,
  }),
  computed: {
    offerSalesTypeField() {
      const fields = {
        cashback_percentage_offer_sales: 'cashback_percentage',
        cashback_absolute_offer_sales: 'cashback_absolute',
        points_offer_sales: 'additional_points_rate',
        next_buy_cashback_offer_sales: 'cashback_absolute_discount',
      }
      return fields[this.offerSalesType]
    },
    isTooltipChangingStatusDisabled() {
      return this.isIndustryCampaign ? !this.isDisabledInRulesByType('active') : true
    },
  },
  methods: {
    isDisabledInRulesByType(field) {
      switch (this.offerSalesType) {
        case 'cashback_absolute_offer_sales':
          return this.isDisabledInRules(this.rules.cashbackAbsolute[field])
        case 'next_buy_cashback_offer_sales':
          return this.isDisabledInRules(this.rules.nextbuyCashback[field])
        default:
          return false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.invalid-input {
  color: $vermelho-0;

  .field-padding {
    span {
      border: 1px #ff5252 solid !important;
      padding: 0 5px;
    }
  }
}

.expanded-itens {
  padding-left: 19%;
}

::v-deep .repeated {
  background-color: #ffc4c4;
  &:hover {
    background-color: #ffc4c4 !important;
  }
}

::v-deep th,
::v-deep td {
  padding: 0 8px;
}

.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
